/**
 * In-app announcement registry.
 *
 * One entry per launch we want members to notice. The host
 * ({@link ../announcements/AnnouncementHost}) shows the first entry that is
 * still active, not yet shown in this browser, and on a member route, and only
 * to signed-in members — so shipping a new announcement is just adding an entry
 * to the top of this list. First displays persist per-announcement in
 * localStorage (same pattern as the guided tour's `tour-*-seen` keys).
 */

export interface Announcement {
  /** Stable slug — keys the localStorage seen flag, never reuse one. */
  id: string;
  /** Small uppercase chip above the title (e.g. "New"). */
  eyebrow: string;
  title: string;
  body: string;
  ctaLabel: string;
  /** In-app route the CTA navigates to. The card never shows there. */
  ctaTo: string;
  /** Optional ISO date after which the announcement stops showing. */
  endsAt?: string;
}

export const ANNOUNCEMENTS: Announcement[] = [
  {
    id: "feed-launch",
    eyebrow: "New",
    title: "The Feed is live",
    body: "Post your plays, trade GIFs, and follow the bettors who actually win. Bet shares drop straight into the composer.",
    ctaLabel: "Check out the Feed",
    ctaTo: "/feed",
  },
];

/**
 * The only routes where an announcement may appear. An allowlist, so a new
 * public page can't pick up the card by default: it pulled trial users off the
 * board in their first session, so content pages, boards, /pricing and the
 * onboarding steps stay out.
 */
const MEMBER_PATHS = [
  "/my-bets",
  "/account-settings",
  "/communities",
  "/profile",
  "/coins",
  "/suggestions",
];

const isUnder = (pathname: string, route: string) =>
  pathname === route || pathname.startsWith(`${route}/`);

/** A first closing line result lands on My Bets, and nothing may cover it. */
export const waitsForChecklist = (pathname: string): boolean =>
  isUnder(pathname, "/my-bets");

const seenKey = (id: string) => `bb-announcement-seen:${id}`;

export const isDismissed = (id: string): boolean => {
  try {
    return localStorage.getItem(seenKey(id)) === "true";
  } catch {
    return true;
  }
};

/** Persist before showing. If storage cannot remember it, skip the popup. */
export const markShown = (id: string): boolean => {
  try {
    localStorage.setItem(seenKey(id), "true");
    return localStorage.getItem(seenKey(id)) === "true";
  } catch {
    return false;
  }
};

export const dismiss = (id: string): void => {
  markShown(id);
};

/** The announcement to show on this route right now, if any. */
export const getActiveAnnouncement = (
  pathname: string,
  now: Date = new Date()
): Announcement | null => {
  if (!MEMBER_PATHS.some((route) => isUnder(pathname, route))) return null;
  for (const a of ANNOUNCEMENTS) {
    if (isDismissed(a.id)) continue;
    if (a.endsAt && now > new Date(a.endsAt)) continue;
    // Already on (or under) the destination — nothing to announce.
    if (isUnder(pathname, a.ctaTo)) continue;
    return a;
  }
  return null;
};
